import type { HandResult, MatchState, RulesConfig, TrickRecord } from '@mendikot/engine';
import {
  encodeView,
  seatKey,
  type Reply,
  type RoomStatus,
  type SeatInfo,
  type SeatKey,
  type StoredView,
} from '@mendikot/protocol';

/** Structured, rules-visible part of the core node (security rules and the sweeper read these). */
export interface RoomMeta {
  roomId: string;
  code: string;
  hostUid: string;
  status: RoomStatus;
  createdAt: number;
  lastActivity: number;
  matchId: string | null;
  /** Bumped when a hand is scored; the archive trigger listens to it. */
  lastScoredHandId: string | null;
}

export interface RoomState {
  version: number;
  updatedAt: number;
  rules: RulesConfig;
  seats: (SeatInfo | null)[];
  turnDeadline: number | null;
  nextHandAt: number | null;
  nextVotes: boolean[];
  rematchVotes: (boolean | null)[];
  nudgeKey: string | null;
  abandonReason: string | null;
  matchCount: number;
  matchStartedAt: number | null;
}

export interface DedupeEntry {
  id: string;
  reply: Reply;
}

export interface HandArchive {
  matchId: string;
  handId: string;
  rules: RulesConfig;
  result: HandResult;
  tricks: TrickRecord[];
  /** `controller: 'bot'` on a human seat means a bot held it when the hand ended (PRD: no silent auto-win). */
  seats: {
    seat: number;
    uid: string | null;
    kind: SeatInfo['kind'];
    controller: SeatInfo['controller'];
    name: string;
  }[];
  score: [number, number];
  matchWinner: 0 | 1 | null;
  at: number;
}

/** Decoded core document. `match` and `dedupe` are server-only and never projected verbatim. */
export interface CoreState {
  meta: RoomMeta;
  members: Record<string, SeatKey>;
  room: RoomState;
  match: MatchState | null;
  dedupe: Record<string, DedupeEntry[]>;
  lastHand: HandArchive | null;
}

/** RTDB shape of /rooms/{roomId}/core. */
export interface EncodedCore {
  meta: RoomMeta;
  members?: Record<string, SeatKey>;
  public: StoredView;
  private?: Record<string, StoredView>;
  secure: { data: string };
}

interface SecureBlob {
  room: RoomState;
  match: MatchState | null;
  dedupe: Record<string, DedupeEntry[]>;
  lastHand: HandArchive | null;
}

export function decodeCore(raw: EncodedCore): CoreState {
  const blob = JSON.parse(raw.secure.data) as SecureBlob;
  return {
    // RTDB drops null fields, so restore them explicitly.
    meta: {
      ...raw.meta,
      matchId: raw.meta.matchId ?? null,
      lastScoredHandId: raw.meta.lastScoredHandId ?? null,
    },
    members: { ...(raw.members ?? {}) },
    room: blob.room,
    match: blob.match,
    dedupe: blob.dedupe ?? {},
    lastHand: blob.lastHand ?? null,
  };
}

export function encodeSecure(state: CoreState): { data: string } {
  const blob: SecureBlob = {
    room: state.room,
    match: state.match,
    dedupe: state.dedupe,
    lastHand: state.lastHand,
  };
  return { data: JSON.stringify(blob) };
}

export function encodeViews(views: {
  public: { version: number };
  private: Record<SeatKey, { version: number }>;
}): Pick<EncodedCore, 'public' | 'private'> {
  const priv: Record<string, StoredView> = {};
  for (const [key, view] of Object.entries(views.private)) priv[key] = encodeView(view);
  return { public: encodeView(views.public), private: priv };
}

export const seatKeys = (players: number): SeatKey[] =>
  Array.from({ length: players }, (_, i) => seatKey(i));
